import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, X, Plus, Minus, Package } from 'lucide-react';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { Permission } from '../auth/permissions';
import { useToast } from '../components/ui/Toast';
import { useCartStore, cartTotals, type CartLine, type PriceInfo } from './cartStore';
import {
  promiseEta,
  type DeliveryCalendar,
  type DeliverySlot,
  type PromiseRule,
} from '../utils/deliveryPromise';
import { round2, brandStyle, PartThumb } from './shared';
import { formatEUR } from '../lib/format';

const eur = (n: number): string => formatEUR(n);

interface AccountCredit {
  credit: { limit: number; outstandingBalance: number; reserved: number; available: number };
  /** Comptant B2B: the order can be paid by card online (CAWL) straight from the cart. */
  cardPayment?: { offered: boolean };
}

/**
 * Right-hand cart panel of the B2B customer portal (MDS Portal composition). A
 * self-service buyer has two standard actions, both posting the cart as authoritative-
 * priced server snapshots:
 *   • Place order   — on-account order (POST /orders), lands CREATED. Over the encours ceiling
 *                     it is refused outright (WP-2 hard block), never queued for approval.
 *   • Request quote — saves a DRAFT quote (POST /quotes) for staff to price/send back.
 */
export function CartPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();
  const user = useAuthStore((s) => s.user);
  const permissionSet = useCapabilitiesStore((s) => s.permissionSet);
  const canQuote = permissionSet.has(Permission.QUOTE_CREATE);
  // B2B_VIEWER is read-only (Part B matrix): no order placement / checkout.
  const canOrder = permissionSet.has(Permission.ORDER_CREATE);

  const lines = useCartStore((s) => s.lines);
  const poRef = useCartStore((s) => s.poRef);
  const setPoRef = useCartStore((s) => s.setPoRef);
  const inc = useCartStore((s) => s.inc);
  const remove = useCartStore((s) => s.remove);
  const clear = useCartStore((s) => s.clear);
  const add = useCartStore((s) => s.add);
  const setQty = useCartStore((s) => s.setQty);

  /**
   * The basket as the buyer reads it: one entry per product, its origins nested beneath. The flat
   * store stays one entry per (product, origin) — that is what has to reach the order as two lines
   * at two prices — this only regroups it for display, our shelf above the ordered-in part.
   */
  const byProduct = useMemo(() => {
    const groups = new Map<
      string,
      {
        productId: string;
        sku: string;
        name: string;
        brand: string | null;
        units: number;
        lines: CartLine[];
      }
    >();
    for (const l of lines) {
      const g = groups.get(l.productId) ?? {
        productId: l.productId,
        sku: l.sku,
        name: l.name,
        brand: l.brand,
        units: 0,
        lines: [],
      };
      g.units += l.qty;
      g.lines.push(l);
      groups.set(l.productId, g);
    }
    for (const g of groups.values()) {
      g.lines.sort((a, b) => (a.source === 'INTERNAL' ? 0 : 1) - (b.source === 'INTERNAL' ? 0 : 1));
    }
    return [...groups.values()];
  }, [lines]);

  /** One delivery per origin: stock leaves now, the ordered-in part follows on its own date. */
  const deliveryCount = useMemo(() => new Set(lines.map((l) => l.source)).size, [lines]);

  const totals = useMemo(() => cartTotals(lines), [lines]);
  const customerId = user?.customerId ?? null;
  const [busy, setBusy] = useState(false);

  // Company credit standing — drives the portal credit widget (GET /b2b/account).
  const [credit, setCredit] = useState<AccountCredit['credit'] | null>(null);
  const [cardOffered, setCardOffered] = useState(false);
  useEffect(() => {
    let alive = true;
    apiClient
      .get<AccountCredit>('/b2b/account')
      .then(({ data }) => {
        if (!alive) return;
        setCredit(data.credit);
        setCardOffered(data.cardPayment?.offered === true);
      })
      .catch(() => {
        /* credit widget is best-effort; ignore failures */
      });
    return () => {
      alive = false;
    };
  }, []);

  const [refused, setRefused] = useState<null | {
    orderTotal: number;
    available: number | null;
    // null = no ceiling (migration 079); an unlimited account is never refused here.
    creditLimit: number | null;
  }>(null);

  /**
   * INV-47 — stock taken from our shelf in the basket was sold before the order went through.
   * Nothing was created; the buyer decides whether the missing units come from a supplier instead
   * (another price, another date) — never switched for them.
   */
  const [stockChanged, setStockChanged] = useState<null | {
    line: CartLine;
    availableQty: number;
    extPrice: PriceInfo | null;
    eta: string | null;
  }>(null);

  const handleStockChanged = async (err: unknown): Promise<boolean> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = (err as any)?.response?.data;
    if (d?.code !== 'STOCK_CHANGED' || !d.details?.productId) return false;
    const line = lines.find((l) => l.productId === d.details.productId && l.source === 'INTERNAL');
    if (!line) return false;
    let extPrice: PriceInfo | null = null;
    let eta: string | null = null;
    try {
      const { data: av } = await apiClient.get<{
        externalPrice: PriceInfo | null;
        supplierAvailable: boolean;
        supplierSlots?: DeliverySlot[];
        supplierPromise?: PromiseRule;
        supplierLeadDays?: number | null;
      }>(`/catalog/products/${line.productId}/availability?customerId=${customerId}`);
      if (av.externalPrice && av.supplierAvailable) {
        extPrice = av.externalPrice;
        // The date WE promise for the units that are missing (2.1), on the same calendar the
        // catalogue quotes with.
        const { data: cal } = await apiClient.get<DeliveryCalendar>('/meta/sales-config');
        const missing = line.qty - Math.max(0, Number(d.details.availableQty ?? 0));
        eta = promiseEta(av.supplierPromise, cal, Date.now(), {
          slots: av.supplierSlots,
          qty: missing,
          leadDays: av.supplierLeadDays,
        }).label;
      }
    } catch {
      /* no live offer — the buyer can still keep what is in stock */
    }
    setStockChanged({
      line,
      availableQty: Math.max(0, Number(d.details.availableQty ?? 0)),
      extPrice,
      eta,
    });
    return true;
  };

  const resolveStockChanged = (toSupplier: boolean) => {
    if (!stockChanged) return;
    const { line, availableQty, extPrice } = stockChanged;
    if (availableQty > 0) setQty(line.productId, 'INTERNAL', availableQty);
    else remove(line.productId, 'INTERNAL');
    if (toSupplier && extPrice) {
      add(
        {
          productId: line.productId,
          sku: line.sku,
          name: line.name,
          brand: line.brand,
          price: extPrice,
          source: 'EXTERNAL',
          switchAgreed: true,
        },
        line.qty - availableQty
      );
    }
    setStockChanged(null);
    push({ tone: 'warning', message: t('portal.cart.stockChanged.basketUpdated') });
  };

  /**
   * A cart line as the server takes it, on an order or a quote.
   *
   * A supplier line with no record yet sends its OFFER; `productId` on such a line is the search
   * row's id and is a basket key, never a product. The server mints (or reuses) the record at
   * that point, so browsing and abandoned baskets leave nothing behind.
   */
  const lineForServer = (l: CartLine) => ({
    ...(l.offer ? { offer: l.offer } : { productId: l.productId }),
    quantity: l.qty,
    // The origin the buyer chose; without it the server re-derives it from stock.
    source: l.source,
    ...(l.switchAgreed ? { switchAgreed: true } : {}),
  });

  const empty = lines.length === 0;
  const ref = poRef.trim() || undefined;

  /* ONE BASKET, ONE ORDER (2026-10-30, same rule as the counter). The command id is the
     basket's, not the click's: a second click, or a retry after "too many requests" or a dropped
     connection, replays the same command and gets the SAME order back instead of a second one.
     It is renewed when the basket or the company changes, i.e. when it is a different order. */
  const basketCommandId = useRef<string>(crypto.randomUUID());
  useEffect(() => {
    basketCommandId.current = crypto.randomUUID();
  }, [lines, customerId]);
  const commandHeaders = () => ({ headers: { 'X-Command-Id': basketCommandId.current } });

  /** `payNow`: a comptant buyer's "order and pay by card" — the order is placed, then the buyer
   *  goes straight to CAWL's page. Coming back lands on /b2b/orders, which asks the server for the
   *  outcome; until CAWL's notification arrives the order simply stays unpaid. */
  const submitOrder = async (payNow = false) => {
    if (!customerId || empty) return;
    setBusy(true);
    try {
      /* Feedback 3.13 — the same order placed twice in a few minutes is refused (409
         DUPLICATE_ORDER) until the buyer says it is a second one. A portal order is confirmed on
         its own and its supplier order fires at once, so this is where a duplicate costs most. */
      const body = {
        customerId,
        deliveryMode: 'COMPLETE',
        customerRef: ref,
        lines: lines.map(lineForServer),
      };
      const post = () =>
        apiClient.post<{ id: string; orderNumber: string; status: string }>(
          '/orders',
          body,
          commandHeaders()
        );
      let placed: { data: { id: string; orderNumber: string; status: string } };
      try {
        placed = await post();
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r = (err as any)?.response;
        const d = (r?.data?.details ?? {}) as { orderNumber?: string; minutesAgo?: number };
        if (
          r?.status === 409 &&
          r?.data?.code === 'DUPLICATE_ORDER' &&
          window.confirm(
            t('portal.cart.duplicateOrderConfirm', {
              number: d.orderNumber ?? '?',
              minutes: d.minutesAgo ?? 0,
            })
          )
        ) {
          placed = await apiClient.post<{ id: string; orderNumber: string; status: string }>(
            '/orders',
            { ...body, confirmDuplicate: true },
            commandHeaders()
          );
        } else throw err;
      }
      const { data } = placed;
      clear();
      if (payNow) {
        try {
          const { data: co } = await apiClient.post<{ checkoutUrl: string }>(
            `/orders/${data.id}/checkout`,
            {},
            { headers: { 'X-Command-Id': crypto.randomUUID() } }
          );
          window.location.href = co.checkoutUrl;
          return;
        } catch (err) {
          // The order exists; only the redirect failed. Show it, with its own "Pay by card".
          showApiError(err);
          navigate(`/orders/${data.id}`);
          return;
        }
      }
      /* A comptant customer may pay this order by card online right away (feedback 6.6): take
         them to the order, where the "Pay by card" button is. Anyone else goes to the list. */
      const offer = await apiClient
        .get<{ offered: boolean }>(`/orders/${data.id}/checkout`)
        .then((r) => r.data.offered)
        .catch(() => false);
      push({
        tone: 'success',
        title: data.orderNumber,
        message: offer ? t('portal.cart.orderPlacedPayByCard') : t('portal.cart.orderPlaced'),
      });
      navigate(offer ? `/orders/${data.id}` : '/b2b/orders');
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp = (err as any)?.response;
      /* INV-38 — the ceiling is a hard stop: nothing was created, reserved or ordered, and there
         is no approval queue and no "pay the overflow by card" way round it. Raising the limit is
         a customer-service decision taken on the account, so that is what the buyer is told. */
      if (resp?.status === 422 && resp?.data?.code === 'CREDIT_LIMIT_EXCEEDED') {
        const d = resp.data.details ?? {};
        setRefused({
          orderTotal: Number(d.orderTotal ?? totals.totalTTC),
          available: d.available == null ? null : Number(d.available),
          creditLimit: d.creditLimit == null ? null : Number(d.creditLimit),
        });
        return;
      }
      if (await handleStockChanged(err)) return;
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const placeOrder = () => submitOrder();
  const placeAndPay = () => submitOrder(true);

  const requestQuote = async () => {
    if (!customerId || empty) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ id: string; quoteNumber?: string }>(
        '/quotes',
        {
          customerId,
          customerRef: ref,
          lines: lines.map(lineForServer),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        title: data.quoteNumber ?? t('portal.cart.quoteFallbackTitle'),
        message: t('portal.cart.quoteRequested'),
      });
      clear();
      navigate(`/quotes/${data.id}`);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const creditRatio =
    credit && credit.limit > 0 ? (credit.outstandingBalance + credit.reserved) / credit.limit : 0;

  return (
    <aside className="col side" aria-label={t('portal.cart.aria')}>
      {credit && credit.limit > 0 && (
        <div className="credit">
          <div className="lab">
            <span>{t('portal.cart.yourCredit')}</span>
            <span className="tnum">
              {eur(credit.outstandingBalance + credit.reserved)} / {eur(credit.limit)}
            </span>
          </div>
          <div className="bar">
            <i
              className={creditRatio > 0.8 ? 'warn' : undefined}
              style={{ width: `${Math.min(100, creditRatio * 100).toFixed(0)}%` }}
            />
          </div>
          <div className="lab">
            <span className="muted">{t('portal.cart.available')}</span>
            <span className="avail-amt tnum">{eur(Math.max(0, credit.available))}</span>
          </div>
        </div>
      )}

      <div className="head">
        <span className="t">
          <ShoppingCart size={16} /> {t('portal.cart.yourCart')}
        </span>
        <span className="muted">{t('portal.cart.refs', { count: lines.length })}</span>
      </div>

      <ul className="clist">
        {empty ? (
          <li>
            <div className="empty" style={{ padding: '32px 16px' }}>
              <div className="ic">
                <ShoppingCart size={24} />
              </div>
              <h2>{t('portal.cart.empty')}</h2>
              <p>{t('portal.cart.emptyHint')}</p>
            </div>
          </li>
        ) : (
          byProduct.map((group) => (
            <li className="citem" key={group.productId}>
              <div className="hd">
                <span className="badge brand" style={brandStyle(group.brand)}>
                  {group.brand ?? '\u2014'}
                </span>
                <span className="cqty tnum">{t('portal.cart.units', { count: group.units })}</span>
              </div>
              <div className="body">
                <div className="thumb" style={{ width: 48, height: 48 }}>
                  <PartThumb size={36} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{group.name}</div>
                  <div className="ref">{t('portal.cart.ref', { sku: group.sku })}</div>
                </div>
              </div>

              {/* ONE product, its origins as SUB-LINES. The same reference never appears twice in
                  the basket, and each origin keeps the price and the date it was chosen at. */}
              {group.lines.map((l) => (
                <div className="csub" key={l.source}>
                  <div className="csub-h">
                    <span
                      className={`obadge ${l.source === 'INTERNAL' ? 'is-ours' : 'is-supplier'}`}
                    >
                      {l.source === 'INTERNAL'
                        ? t('portal.cart.inStock')
                        : t('portal.cart.onOrder')}
                    </span>
                    <button
                      className="x"
                      onClick={() => remove(l.productId, l.source)}
                      aria-label={t('portal.cart.removeOrigin', {
                        origin:
                          l.source === 'INTERNAL'
                            ? t('portal.cart.originInStock')
                            : t('portal.cart.originOnOrder'),
                        sku: l.sku,
                      })}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="ft">
                    <div className="stepper">
                      <button
                        onClick={() => inc(l.productId, l.source, -1)}
                        aria-label={t('portal.cart.less')}
                      >
                        <Minus size={13} />
                      </button>
                      <span className="q tnum">{l.qty}</span>
                      <button
                        onClick={() => inc(l.productId, l.source, 1)}
                        aria-label={t('portal.cart.plus')}
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                    <div>
                      <div className="lt tnum">{eur(round2(l.qty * l.price.finalUnitPriceHT))}</div>
                      <div className="u tnum">
                        {eur(l.price.finalUnitPriceHT)} {t('portal.cart.each')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </li>
          ))
        )}
      </ul>

      <div className="totals">
        {/* Said before validating: two origins is two shipments on two dates, and the buyer is
            about to commit on the strength of the dates they just chose. */}
        {deliveryCount > 1 && (
          <div className="blnote">
            <Package size={13} />
            <span>
              <Trans
                i18nKey="portal.cart.deliveriesNote"
                values={{ count: deliveryCount }}
                components={{ 1: <b /> }}
              />
            </span>
          </div>
        )}
        <div className="ln">
          <span className="muted">{t('portal.cart.items', { count: totals.units })}</span>
          <span className="tnum">{eur(totals.subtotalHT)}</span>
        </div>
        <div className="ln">
          <span className="muted">{t('portal.cart.depositFee')}</span>
          <span className="tnum">{eur(totals.depositHT)}</span>
        </div>
        <div className="ln">
          <span className="muted">{t('common.totalHT')}</span>
          <span className="tnum">{eur(totals.totalHT)}</span>
        </div>
        <div className="ln">
          <span className="muted">{t('portal.cart.vat20')}</span>
          <span className="tnum">{eur(totals.vat)}</span>
        </div>
        <div className="ln grand">
          <span>{t('common.totalTTC')}</span>
          <span className="tnum">{eur(totals.totalTTC)}</span>
        </div>

        <div className="pofield">
          <span className="ic">
            <Package size={14} />
          </span>
          <input
            placeholder={t('portal.cart.poRef')}
            value={poRef}
            onChange={(e) => setPoRef(e.target.value)}
          />
        </div>

        {canOrder && (
          <button
            className="btn btn--primary btn--block btn--lg"
            style={{ marginTop: 'var(--s-3)' }}
            disabled={busy || empty || !customerId}
            onClick={placeOrder}
          >
            {busy ? t('portal.cart.processing') : t('portal.cart.placeOrder')}
          </button>
        )}
        {canOrder && cardOffered && (
          <button
            className="btn btn--ghost btn--block"
            style={{ marginTop: 'var(--s-2)' }}
            disabled={busy || empty || !customerId}
            onClick={placeAndPay}
          >
            {t('portal.cart.placeAndPayByCard')}
          </button>
        )}
        {canQuote && (
          <button
            className="btn btn--ghost btn--block"
            style={{ marginTop: 'var(--s-2)' }}
            disabled={busy || empty}
            onClick={requestQuote}
          >
            {t('portal.cart.requestQuote')}
          </button>
        )}
        {!canOrder && !canQuote && (
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            {t('portal.cart.readOnly')}
          </div>
        )}
        {canOrder && !customerId && (
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            {t('portal.cart.noCompany')}
          </div>
        )}
      </div>

      {stockChanged && (
        <div className="mds-modal-bd" onClick={() => setStockChanged(null)}>
          <div
            className="mds-modal sm"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 20 }}
          >
            <h3
              style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' }}
            >
              {t('portal.cart.stockChanged.title')}
            </h3>
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              {t('portal.cart.stockChanged.detail', {
                sku: stockChanged.line.sku,
                requested: stockChanged.line.qty,
                available: stockChanged.availableQty,
              })}{' '}
              {stockChanged.extPrice
                ? t('portal.cart.stockChanged.supplierOffer', {
                    missing: stockChanged.line.qty - stockChanged.availableQty,
                    price: eur(stockChanged.extPrice.finalUnitPriceHT),
                    eta: stockChanged.eta ?? '',
                  })
                : t('portal.cart.stockChanged.noSupplier')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stockChanged.extPrice && (
                <button className="btn btn--primary" onClick={() => resolveStockChanged(true)}>
                  {t('portal.cart.stockChanged.switch', {
                    missing: stockChanged.line.qty - stockChanged.availableQty,
                  })}
                </button>
              )}
              <button className="btn btn--ghost" onClick={() => resolveStockChanged(false)}>
                {stockChanged.availableQty > 0
                  ? t('portal.cart.stockChanged.keepInStock', {
                      available: stockChanged.availableQty,
                    })
                  : t('portal.cart.stockChanged.removeLine')}
              </button>
              <button
                onClick={() => setStockChanged(null)}
                className="muted"
                style={{ background: 'none', border: 'none', fontSize: 12 }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {refused && (
        <div className="mds-modal-bd" onClick={() => setRefused(null)}>
          <div
            className="mds-modal sm"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 20 }}
          >
            <h3
              style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' }}
            >
              {t('portal.cart.refusedTitle')}
            </h3>
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              {t('portal.cart.refusedBody', { total: eur(refused.orderTotal) })}
              {refused.available != null &&
                ` ${t('portal.cart.refusedAvailable', { available: eur(Math.max(0, refused.available)) })}`}
            </p>
            <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
              {t('portal.cart.refusedContact')}
            </p>
            <button
              className="btn btn--primary"
              style={{ width: '100%' }}
              onClick={() => setRefused(null)}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
